import React, { FunctionComponent } from 'react'
import { Button, Heading, Modal, Text } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation } from '../../utils/convergence'
import styles from './style.module.css'
import { InstanceProps } from 'react-modal-promise'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent, TrackingEventValue } from '../../model/monitoring'

interface TrackingEvents {
  viewed: TrackingEventValue
  leave: TrackingEventValue
  cancel: TrackingEventValue
  stay?: TrackingEventValue
}

const defaultTrackingEvents: TrackingEvents = {
  viewed: TrackingEvent.VIEWED_LEAVE_CHECKOUT_MODAL,
  leave: TrackingEvent.PRESSED_LEAVE_CHECKOUT_MODAL_LEAVE,
  cancel: TrackingEvent.PRESSED_LEAVE_CHECKOUT_MODAL_CANCEL,
}

interface Props {
  isOpen: boolean
  onResolve?: () => void
  onReject?: () => void
  messageContext?: string
  invertedButtons?: boolean
  trackingEvents?: TrackingEvents
}

export const LeaveCheckoutModal: FunctionComponent<Props & InstanceProps<unknown>> = ({
  isOpen,
  onResolve,
  onReject,
  messageContext = 'consumerLending',
  invertedButtons = false,
  trackingEvents = defaultTrackingEvents,
}) => {
  const { textSize, headingSize } = useCheckoutSizes()
  const { t } = useTranslation()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const handleLeave = (): void => {
    logEvent(trackingEvents.leave)
    onResolve?.()
  }

  const handleCancel = (): void => {
    logEvent(trackingEvents.cancel)
    onReject?.()
  }

  const handleStay = (): void => {
    if (trackingEvents.stay !== undefined) {
      logEvent(trackingEvents.stay)
    }
    onReject?.()
  }

  return (
    <Modal show={isOpen} onClose={handleCancel} showCloseButton>
      <div className={styles.container}>
        <Modal.Header>
          <Heading size={headingSize}>{t('common:leaveCheckoutModal:title')}</Heading>
          <Text size={textSize}>
            {messageContext === 'paylater'
              ? t('common:leaveCheckoutModal:message', { context: 'paylater' })
              : t('common:leaveCheckoutModal:message', { context: 'consumerLending' })}
          </Text>
        </Modal.Header>
        <Modal.Content>
          {invertedButtons ? (
            <>
              <Button kind='Primary' padding='Fluid' size='Large' testNameSpace='leave-checkout-modal-stay' onClick={handleStay}>
                {t('common:leaveCheckoutModal:ctaStay')}
              </Button>
              <div className={styles.cancelButton}>
                <Button kind='Ghost' padding='Fluid' size='Large' testNameSpace='leave-checkout-modal-leave' onClick={handleLeave}>
                  {t('common:leaveCheckoutModal:cta')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button kind='Primary' padding='Fluid' size='Large' testNameSpace='leave-checkout-modal-leave' onClick={handleLeave}>
                {t('common:leaveCheckoutModal:cta')}
              </Button>
              <div className={styles.cancelButton}>
                <Button kind='Ghost' padding='Fluid' size='Large' testNameSpace='leave-checkout-modal-cancel' onClick={handleCancel}>
                  {t('common:cancel')}
                </Button>
              </div>
            </>
          )}
        </Modal.Content>
      </div>
      <TrackOnMount eventName={trackingEvents.viewed} />
    </Modal>
  )
}
